<template lang="pug">
v-input.tiptap(:label="label" :class="{ focused: focused || linkMenuIsActive, active: focused || linkMenuIsActive, disabled }")
  .tiptap-wrap.pt-5.mt-1(v-if="disabled")
    div(v-html="value")
  .tiptap-wrap(v-else-if="editHtml")
    .menu-bar.is-hidden(:class="{ 'is-not-hidden': focused }")
      .v-btn-toggle.v-btn-toggle--selected
        v-btn(:input-value="editHtml" @click="toggleHtml" flat): v-icon description
    textarea.html-textarea(ref="textarea" :value="value" @input="htmlChanged" @focus="focused = true" @blur="focused = false; toggleHtml(false)")
  .tiptap-wrap(v-else)
    editor-menu-bar(:editor="editor" v-slot="{ commands, isActive, focused, getMarkAttrs, getMarkRange }")
      .menu-bar.is-hidden(:class="{ 'is-not-hidden': focused || linkMenuIsActive }")
        .v-btn-toggle.v-btn-toggle--selected
          v-btn(:input-value="isActive.bold()" @click="commands.bold" flat): v-icon format_bold
          v-btn(:input-value="isActive.italic()" @click="commands.italic" flat): v-icon format_italic
          v-btn(:input-value="isActive.underline()" @click="commands.underline" flat): v-icon format_underline
          v-btn(:input-value="isActive.bullet_list()" @click="commands.bullet_list" flat): v-icon format_list_bulleted
          v-menu(:value="linkMenuIsActive" top offset-y :open-on-click="false" :close-on-click="!false" :close-on-content-click="false" @input="menuClick")
            v-btn(slot="activator" :input-value="isActive.link()" @click="showLinkMenu(getMarkAttrs('link'))" flat): v-icon insert_link
            v-form(@submit.prevent="setLinkUrl(commands.link, linkUrl)")
              v-text-field.link-menu(v-model='linkUrl' placeholder='https://' ref='linkInput' @keydown.esc='hideLinkMenu' outline single-line hide-details prepend-inner-icon="delete" @click:append="setLinkUrl(commands.link, linkUrl)" append-icon="insert_link" @click:prepend-inner="setLinkUrl(commands.link, null)")
          v-btn(:input-value="editHtml" @click="toggleHtml" flat): v-icon description
    editor-content(:editor="editor")

</template>

<style lang="stylus" scoped>

>>> blockquote
  border-left 3px solid #ccc
  margin-left 2px
  padding-left 2px

.tiptap-wrap
  width 100%

.menu-bar
  padding-top: 0.5em
  padding-bottom: 0.5em

.html-textarea
  width 100%

.link-menu
  background white

>>> .ProseMirror
  width: 100%
  min-height: 1em
  outline: none

.is-hidden
  transition-duration 0.3s
  opacity 0
  visibility hidden

.is-not-hidden
  opacity 1
  visibility visible

.tiptap >>>
  .v-label
    max-width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
    top: 20px;
    transform-origin: top left;
    white-space: nowrap;
    pointer-events: none;

    position: absolute !important

>>>
  .v-input__control > .v-input__slot::after
    border-color: #0053a6;
    border-style: solid;
    border-width: thin 0 thin 0;
    transform: scaleX(0);

  .v-input__control > .v-input__slot::before
    border-style: solid;
    border-width: thin 0 0 0;
    border-color: rgba(0,0,0,0.42);

  .v-input__control > .v-input__slot::before, .v-input__control > .v-input__slot::after
    bottom: -1px;
    content: '';
    left: 0;
    position: absolute;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    width: 100%;

.theme--light:not(.v-input--has-state) >>> .v-input__control > .v-input__slot:hover::before
  border-color: rgba(0,0,0,0.87);

.tiptap.disabled >>>
  color rgba(0, 0, 0, 0.38)
  .v-input__control > .v-input__slot::before
    border-image: repeating-linear-gradient(to right, rgba(0,0,0,0.38) 0px, rgba(0,0,0,0.38) 2px, transparent 2px, transparent 4px) 1 repeat

  .v-label
    color: rgba(0,0,0,0.38);

.tiptap.focused >>> 
  .v-label
    color: #0053a6

  .v-input__control > .v-input__slot::after
      transform: scaleX(1);

.tiptap.active >>>, .tiptap.focused >>>
  .v-label
    max-width: 133%;
    transform: translateY(-18px) scale(0.75);
    opacity: 0;

</style>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Strike,
  Underline,
  History,
} from 'tiptap-extensions'
import TiptapLink from './tiptap-link'

export default {
  name: "tiptap-input",
  props: {
    value: String,
    label: String,
    disabled: {
      type: Boolean,
      default: false
    },
  },
  components: {
    EditorMenuBar,
    EditorContent,
  },
  data() {
    return {
      linkUrl: null,
      linkMenuIsActive: false,
      focused: false,
      editHtml: false,
      lastHtml: this.value || '',
      editor: new Editor({
        content: this.value || '',
        onFocus: () => {
          this.focused = true
        },
        onBlur: () => {
          this.focused = false
        },
        onUpdate: ({ getHTML }) => {
          const html = getHTML()
          this.lastHtml = html
          this.$emit('input', html)
        },
        emptyDocument: {
          type: "doc",
          "content":[{ type: "text", text: "empty" }]
        },
        extensions: [
          new Blockquote(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Code(),
          new Italic(),
          new TiptapLink(),
          new Strike(),
          new Underline(),
          new History(),
        ]
      }),
    }
  },
  watch: {
    value(value) {
      if (this.editHtml) {
        this.growTextArea()
      } else if (value !== this.lastHtml) {
        this.editor.setContent(value || '')
      }
    }
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    htmlChanged(event) {
      this.$emit('input', event.target.value)
    },
    toggleHtml(html) {
      if (html === false || this.editHtml) {
        this.editHtml = false
        this.$nextTick(() => {
          this.editor.setContent(this.value || '', true)
          this.editor.focus()
        })
      } else {
        this.editHtml = true
        this.$nextTick(() => {
          this.growTextArea()
          this.$refs.textarea.focus()
        })
      }
    },
    growTextArea() {
      const textarea = this.$refs.textarea
      if (textarea && textarea.clientHeight < textarea.scrollHeight) {
        textarea.style.minHeight = textarea.scrollHeight + "px";
      }
    },
    showLinkMenu(attrs) {
      if (this.linkMenuIsActive) {
        this.$nextTick(() => {
          this.hideLinkMenu()
        })
      } else {
        this.linkUrl = attrs.href
        this.linkMenuIsActive = true
        this.$nextTick(() => {
          this.$refs.linkInput.focus()
          this.$nextTick(() => {
            this.$refs.linkInput.focus()
            this.$nextTick(() => {
              this.$refs.linkInput.focus()
            })
          })
        })
      }
    },
    menuClick(open) {
      if (!open) {
        this.hideLinkMenu()
      }
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
      command({ href: url })
      this.hideLinkMenu()
      this.editor.focus()
    },
  }
}
</script>
